const reactCheatSheet = {
  id: "react-cheatsheet",
  title: "React CheatSheet",
  description:
    "A comprehensive guide to React concepts, hooks, components, and best practices for modern React development.",
  language: "javascript",
  category: "Frontend",
  level: "All Levels",
  sections: [
    {
      title: "Components",
      items: [
        {
          title: "Functional Component",
          content: "Modern way to write React components using functions.",
          code: `// Basic functional component
const Welcome = ({ name }) => {
  return <h1>Hello, {name}!</h1>;
};

// With multiple props and children
const Card = ({ title, subtitle, children }) => {
  return (
    <div className="card">
      <h2>{title}</h2>
      <h3>{subtitle}</h3>
      {children}
    </div>
  );
};`,
          note: "Functional components are the preferred way to write components in modern React. They're simpler, more readable, and support hooks.",
        },
        {
          title: "Class Component",
          content:
            "Traditional way to create React components with lifecycle methods.",
          code: `class Welcome extends React.Component {
  constructor(props) {
    super(props);
    this.state = { count: 0 };
  }

  componentDidMount() {
    console.log('Component mounted');
  }

  render() {
    return <h1>Hello, {this.props.name}!</h1>;
  }
}`,
          note: "While class components are still supported, functional components with hooks are recommended for new code.",
        },
      ],
    },
    {
      title: "Props & State",
      items: [
        {
          title: "Props",
          content: "Pass data from parent to child components.",
          code: `// Parent component
const Parent = () => {
  return <Child name="John" age={25} isAdmin={true} />;
};

// Child component
const Child = (props) => {
  // Destructuring props
  const { name, age, isAdmin } = props;
    return (
      <div>
      <p>Name: {name}</p>
      <p>Age: {age}</p>
      {isAdmin && <p>Admin user</p>}
      </div>
    );
};`,
          note: "Props are read-only and help create reusable components. Use destructuring for cleaner code.",
        },
        {
          title: "State Management",
          content: "Handle component-level state using useState hook.",
          code: `const Counter = () => {
  const [count, setCount] = useState(0);
  const [user, setUser] = useState({ name: '', age: 0 });

  return (
    <div>
      <p>Count: {count}</p>
      <button onClick={() => setCount(count + 1)}>Increment</button>
      
      {/* Updating object state */}
      <button onClick={() => setUser({ ...user, name: 'John' })}>
        Update Name
      </button>
    </div>
  );
};`,
          note: "Always use the setter function for state updates. For objects and arrays, make sure to spread existing values.",
        },
      ],
    },
    {
      title: "Hooks",
      items: [
        {
          title: "useState",
          content: "Manage state in functional components.",
          code: `const [state, setState] = useState(initialValue);

// Examples
const [count, setCount] = useState(0);
const [user, setUser] = useState(null);
const [items, setItems] = useState([]);

// Function updates
setCount(prevCount => prevCount + 1);
setItems(prevItems => [...prevItems, newItem]);`,
          note: "Use the functional update form when new state depends on previous state.",
        },
        {
          title: "useEffect",
          content: "Handle side effects in components.",
          code: `// Run on every render
useEffect(() => {
  console.log('Component updated');
});

// Run only on mount
useEffect(() => {
  console.log('Component mounted');
}, []);

// Run when dependencies change
useEffect(() => {
  console.log('Count changed:', count);
}, [count]);

// Cleanup on unmount
useEffect(() => {
  const subscription = api.subscribe();
  return () => {
    subscription.unsubscribe();
  };
}, []);`,
          note: "Always include dependencies in the dependency array. Use cleanup functions to prevent memory leaks.",
        },
        {
          title: "useContext",
          content: "Consume context in components without prop drilling.",
          code: `// Create context
const ThemeContext = React.createContext('light');

// Provider
const App = () => {
  return (
    <ThemeContext.Provider value="dark">
      <MainContent />
    </ThemeContext.Provider>
  );
};

// Consumer
const MainContent = () => {
  const theme = useContext(ThemeContext);
  return <div className={theme}>Content</div>;
};`,
          note: "Context is great for global state like themes, user data, or language preferences.",
        },
      ],
    },
    {
      title: "Advanced Hooks",
      items: [
        {
          title: "useReducer",
          content: "Manage complex state logic with reducers.",
          code: `const initialState = { count: 0 };

function reducer(state, action) {
  switch (action.type) {
    case 'increment':
      return { count: state.count + 1 };
    case 'decrement':
      return { count: state.count - 1 };
    default:
      return state;
  }
}

const Counter = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  
  return (
    <div>
      Count: {state.count}
      <button onClick={() => dispatch({ type: 'increment' })}>+</button>
      <button onClick={() => dispatch({ type: 'decrement' })}>-</button>
    </div>
  );
};`,
          note: "useReducer is preferable over useState for complex state logic or when state updates depend on multiple values.",
        },
        {
          title: "useMemo & useCallback",
          content: "Optimize performance by memoizing values and functions.",
          code: `// Memoize expensive calculations
const memoizedValue = useMemo(() => {
  return expensiveCalculation(count);
}, [count]);

// Memoize callbacks
const memoizedCallback = useCallback(() => {
  doSomething(count);
}, [count]);

// Example with both
const MemoComponent = ({ items }) => {
  const sortedItems = useMemo(() => {
    return items.sort((a, b) => b - a);
  }, [items]);

  const handleClick = useCallback((id) => {
    console.log('Item clicked:', id);
  }, []);
  
  return (
    <ul>
      {sortedItems.map(item => (
        <li key={item} onClick={() => handleClick(item)}>
          {item}
        </li>
      ))}
    </ul>
  );
};`,
          note: "Only use these hooks when there's a clear performance benefit. Premature optimization can make code more complex.",
        },
      ],
    },
    {
      title: "Event Handling",
      items: [
        {
          title: "Event Handlers",
          content: "Handle user interactions in React components.",
          code: `const FormComponent = () => {
  const handleSubmit = (e) => {
    e.preventDefault();
    // Form submission logic
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleClick = (id) => (e) => {
    e.stopPropagation();
    console.log('Item clicked:', id);
  };
  
  return (
    <form onSubmit={handleSubmit}>
      <input
        name="username"
        onChange={handleChange}
        onClick={(e) => e.target.select()}
      />
      <button type="submit">Submit</button>
    </form>
  );
};`,
          note: "Use camelCase for event handlers. Remember to prevent default behavior when needed.",
        },
      ],
    },
    {
      title: "Conditional Rendering",
      items: [
        {
          title: "Conditional Rendering Patterns",
          content: "Different ways to conditionally render content.",
          code: `// If condition
{isLoggedIn && <UserDashboard />}

// If-else condition
{isLoggedIn ? <UserDashboard /> : <LoginForm />}

// Multiple conditions
{status === 'loading' && <Spinner />}
{status === 'error' && <Error message={errorMessage} />}
{status === 'success' && <Data />}

// With switch statement
{(() => {
  switch(status) {
    case 'loading':
      return <Spinner />;
    case 'error':
      return <Error />;
    default:
      return <Data />;
  }
})()}`,
          note: "Choose the pattern that makes your code most readable. Avoid complex conditions in JSX.",
        },
      ],
    },
    {
      title: "Lists & Keys",
      items: [
        {
          title: "Rendering Lists",
          content: "Properly render lists of items with keys.",
          code: `const ListComponent = ({ items }) => {
  return (
    <ul>
      {items.map((item) => (
        <li key={item.id}>
          {item.name}
        </li>
      ))}
    </ul>
  );
};

// With index as key (avoid if possible)
const SimpleList = ({ items }) => {
  return (
    <ul>
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
};

// Complex list with multiple elements
const ProductList = ({ products }) => {
  return (
    <div className="product-grid">
      {products.map(product => (
        <div key={product.id} className="product-card">
          <img src={product.image} alt={product.name} />
          <h3>{product.name}</h3>
          <p>{product.price}</p>
        </div>
      ))}
    </div>
  );
};`,
          note: "Always use unique and stable keys. Avoid using array index as keys when the list can change.",
        },
      ],
    },
    {
      title: "Forms",
      items: [
        {
          title: "Form Handling",
          content: "Handle form inputs and submissions in React.",
          code: `const FormExample = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await submitForm(formData);
      // Handle success
    } catch (error) {
      // Handle error
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <input
        name="username"
        value={formData.username}
        onChange={handleChange}
      />
      <input
        type="email"
        name="email"
        value={formData.email}
        onChange={handleChange}
      />
      <input
        type="password"
        name="password"
        value={formData.password}
        onChange={handleChange}
      />
      <button type="submit">Submit</button>
    </form>
  );
};`,
          note: "Use controlled components for form inputs. Consider using form libraries like Formik or React Hook Form for complex forms.",
        },
      ],
    },
    {
      title: "Routing (React Router)",
      items: [
        {
          title: "Basic Routing Setup",
          content: "Setting up React Router with basic routes and navigation.",
          code: `// App.js
import { BrowserRouter, Routes, Route, Link, Navigate } from 'react-router-dom';

const App = () => {
  return (
    <BrowserRouter>
      {/* Navigation */}
      <nav>
        <Link to="/">Home</Link>
        <Link to="/about">About</Link>
        <Link to="/users">Users</Link>
      </nav>
      
      {/* Route Definitions */}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/users" element={<Users />}>
          <Route path=":id" element={<UserProfile />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};`,
          note: "React Router v6 uses a declarative approach to routing. Use nested routes for complex layouts.",
        },
        {
          title: "Route Hooks & Navigation",
          content:
            "Using React Router hooks for navigation and route information.",
          code: `// Route hooks example
const UserProfile = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Get query parameters
  const filter = searchParams.get('filter');

  // Programmatic navigation
  const handleClick = () => {
    navigate('/dashboard', { 
      state: { from: location.pathname },
      replace: true 
    });
  };
  
  return (
    <div>
      <h2>User ID: {id}</h2>
      <p>Filter: {filter}</p>
      <button onClick={handleClick}>Go to Dashboard</button>
    </div>
  );
};`,
          note: "Use hooks like useNavigate for programmatic navigation, useParams for route parameters, and useLocation for current location info.",
        },
      ],
    },
    {
      title: "DOM Manipulation & Refs",
      items: [
        {
          title: "Using Refs",
          content: "Accessing and manipulating DOM elements with refs.",
          code: `// Basic ref usage
const InputFocus = () => {
  const inputRef = useRef(null);

  useEffect(() => {
    // Focus input on mount
    inputRef.current?.focus();
  }, []);

  return <input ref={inputRef} />;
};

// ForwardRef example
const CustomInput = forwardRef((props, ref) => {
  return <input ref={ref} {...props} />;
});

// Measuring DOM nodes
const MeasureExample = () => {
  const [height, setHeight] = useState(0);
  
  const measuredRef = useCallback(node => {
    if (node !== null) {
      setHeight(node.getBoundingClientRect().height);
    }
  }, []);

  return (
    <div ref={measuredRef}>
      Height: {height}px
    </div>
  );
};`,
          note: "Use refs sparingly and only when direct DOM access is necessary. Prefer React's declarative updates when possible.",
        },
      ],
    },
    {
      title: "DOM Events",
      items: [
        {
          title: "Event Handling",
          content: "Handling various DOM events in React components.",
          code: `const EventsExample = () => {
  // Mouse events
  const handleClick = (e) => {
    console.log('Click position:', e.clientX, e.clientY);
  };

  // Keyboard events
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      console.log('Enter pressed');
    }
  };

  // Form events
  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    console.log(Object.fromEntries(formData));
  };

  // Event propagation
  const handleBubble = (e) => {
    e.stopPropagation();
    console.log('Button clicked');
  };

  return (
    <div onClick={() => console.log('Outer div clicked')}>
      <button 
        onClick={handleBubble}
        onMouseEnter={() => console.log('Mouse enter')}
        onMouseLeave={() => console.log('Mouse leave')}
      >
        Click Me
      </button>

      <input
        onKeyPress={handleKeyPress}
        onChange={(e) => console.log('Value:', e.target.value)}
        onFocus={() => console.log('Input focused')}
        onBlur={() => console.log('Input blurred')}
      />

      <form onSubmit={handleSubmit}>
        <input name="username" />
        <button type="submit">Submit</button>
      </form>
    </div>
  );
};`,
          note: "React events are synthetic events that normalize browser differences. They work similarly to native DOM events but with consistent behavior across browsers.",
        },
      ],
    },
    {
      title: "Performance Optimization",
      items: [
        {
          title: "React.memo and Pure Components",
          content: "Prevent unnecessary re-renders with memoization.",
          code: `// Memoize component
const ExpensiveComponent = React.memo(({ data }) => {
  return <div>{/* Expensive render */}</div>;
}, (prevProps, nextProps) => {
  // Custom comparison function
  return prevProps.data.id === nextProps.data.id;
});

// With hooks
const MemoizedComponent = () => {
  const [count, setCount] = useState(0);
  
  // Memoize expensive calculation
const expensiveValue = useMemo(() => {
    return computeExpensiveValue(count);
  }, [count]);

  // Memoize callback
  const handleClick = useCallback(() => {
    setCount(c => c + 1);
  }, []);

  return (
    <div>
      <h1>{expensiveValue}</h1>
      <button onClick={handleClick}>
        Increment
      </button>
    </div>
  );
};`,
          note: "Use memoization techniques only when there's a measurable performance benefit. Profile your app first.",
        },
        {
          title: "Code Splitting & Lazy Loading",
          content: "Optimize bundle size with dynamic imports.",
          code: `// Lazy load component
const LazyComponent = React.lazy(() => 
  import('./HeavyComponent')
);

// Use with Suspense
const App = () => {
  return (
    <Suspense fallback={<Loading />}>
      <LazyComponent />
    </Suspense>
  );
};

// Route-based code splitting
const Routes = () => {
  return (
    <Routes>
      <Route 
        path="/dashboard" 
        element={
          <Suspense fallback={<Loading />}>
            <LazyDashboard />
          </Suspense>
        } 
      />
    </Routes>
  );
};`,
          note: "Use code splitting for large components that aren't immediately needed. Common for routes and modals.",
        },
      ],
    },
    {
      title: "Error Handling",
      items: [
        {
          title: "Error Boundaries",
          content: "Catching and handling errors in React components.",
          code: `// Error Boundary Component
class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };
  
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  
  componentDidCatch(error, info) {
    // Log error to service
    logErrorToService(error, info);
  }
  
  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />;
    }
    return this.props.children;
  }
}

// Using Error Boundary
const App = () => {
  return (
    <ErrorBoundary>
      <ComponentThatMightError />
    </ErrorBoundary>
  );
};

// Custom error hook
const useError = () => {
  const [error, setError] = useState(null);
  
  const handleError = async (fn) => {
    try {
      await fn();
    } catch (err) {
      setError(err);
      // Handle error
    }
  };

  return { error, handleError };
};`,
          note: "Error boundaries catch render errors. Use try-catch for event handlers and async code.",
        },
      ],
    },
    {
      title: "Testing",
      items: [
        {
          title: "Component Testing",
          content: "Testing React components with React Testing Library.",
          code: `// Component test example
import { render, screen, fireEvent } from '@testing-library/react';

test('renders counter and increments', () => {
  // Render component
  render(<Counter />);
  
  // Find elements
  const counter = screen.getByText(/count:/i);
  const button = screen.getByRole('button', { name: /increment/i });
  
  // Initial state
  expect(counter).toHaveTextContent('Count: 0');
  
  // Interact with component
  fireEvent.click(button);
  
  // Check updated state
  expect(counter).toHaveTextContent('Count: 1');
});

// Async test
test('loads and displays data', async () => {
  render(<DataComponent />);
  
  // Wait for loading
  expect(screen.getByText(/loading/i)).toBeInTheDocument();
  
  // Wait for data
  const items = await screen.findAllByRole('listitem');
  expect(items).toHaveLength(3);
});

// Mock test
jest.mock('./api', () => ({
  fetchData: jest.fn(() => Promise.resolve({ data: [] }))
}));`,
          note: "Focus on testing behavior rather than implementation. Use React Testing Library for user-centric tests.",
        },
      ],
    },
  ],
};

export default reactCheatSheet;
